import type { StringKey } from './i18n.ts'

/**
 * Whether the "Move to Zoom" confirm is enabled in the fallback dialog.
 *
 * Pure and React-free so the fix-before-Zoom rule can be unit-tested directly.
 *
 * Device faults need a completed local recovery, or time for the requested
 * student to respond. Connection failures retain the one-attempt escape hatch.
 * A reason must always be chosen first — the server refuses a reasonless switch.
 *
 * Independently, Zoom stays locked until five minutes after official session
 * start. Early joiners were flipping out of habit; Fix still works immediately.
 */

/**
 * The "Something else" reason value. English on purpose: it is sent to the server
 * verbatim and SessionSwitchTicket.categorize_reason buckets it by regex. Shared
 * with FallbackDialog so the dialog option and this gate can never drift apart.
 */
export const OTHER_REASON = 'Something else'
export const CONNECTION_REASON = 'Connection keeps dropping'
export const STUDENT_FIX_DWELL_MS = 30_000

export const ZOOM_UNLOCK_AFTER_MS = 5 * 60 * 1000

export interface ZoomConfirmGate {
  /** Set when the fault is the student's own hardware — see Classroom. */
  studentDeviceReason?: string | null
  /** The English reason value the tutor picked, or null if none yet. */
  reason: string | null
  /** recovery.attempts — cumulative recoveries this session, auto or manual. */
  recoveryAttempts: number
  recoveryCompleted?: boolean
  canAskStudent?: boolean
  askSentAt?: number | null
  studentAnswered?: boolean
  /** ISO session start from the API. Missing/invalid → Zoom stays locked. */
  sessionStartAt: string
  now?: number
}

export function zoomUnlockAt(sessionStartAt: string): number | null {
  const start = Date.parse(sessionStartAt)
  return Number.isFinite(start) ? start + ZOOM_UNLOCK_AFTER_MS : null
}

export function zoomTooEarly(sessionStartAt: string, now = Date.now()): boolean {
  const unlock = zoomUnlockAt(sessionStartAt)
  return unlock === null || now < unlock
}

export function zoomConfirmEnabled({
  reason,
  recoveryAttempts,
  recoveryCompleted = false,
  studentDeviceReason,
  canAskStudent = false,
  askSentAt,
  studentAnswered = false,
  sessionStartAt,
  now = Date.now(),
}: ZoomConfirmGate): boolean {
  if (reason === null) return false
  if (zoomTooEarly(sessionStartAt, now)) return false
  if (reason === CONNECTION_REASON) return recoveryAttempts >= 1 || askSentAt != null
  if ((studentDeviceReason && canAskStudent) || askSentAt != null) {
    return askSentAt != null && (studentAnswered || now - askSentAt >= STUDENT_FIX_DWELL_MS)
  }
  return recoveryAttempts >= 1 && recoveryCompleted
}

/**
 * Which student-device reason (i18n key) the fallback dialog leads with, or null.
 * Mic faults outrank camera; a missing camera routes only when the class is 1:1
 * (`soloClass` = session.type === '1:1', NOT the count of faulted students);
 * a blocked camera routes in any class. See Classroom for the rationale.
 */
export function studentDeviceReasonKey({
  micFaults,
  camFaults,
  soloClass,
}: {
  micFaults: string[]
  camFaults: string[]
  soloClass: boolean
}): StringKey | null {
  for (const access of micFaults) {
    if (access === 'blocked') return 'fallback.deviceMicBlocked'
    if (access === 'missing') return 'fallback.deviceNoMic'
    if (access === 'silent') return 'fallback.deviceMicSilent'
    if (access === 'busy') return 'fallback.deviceMicBusy'
  }
  for (const access of camFaults) {
    if (access === 'blocked') return 'fallback.deviceCamBlocked'
    if (access === 'busy') return 'fallback.deviceCamBusy'
    if (access === 'missing' && soloClass) return 'fallback.deviceNoCamera'
  }
  return null
}
